/**
 * AdminMadrassas — Global Admin UI for managing partner madrassas (Lot 34).
 *
 * Route: /admin/madrassas (admin-only)
 *
 * Lists all madrassas with stats (classes, teachers, students, pending
 * teacher requests). Provides search, status filter, and per-row actions:
 * see detail (/admin/madrassas/:id), edit (inline dialog), suspend, reactivate,
 * archive, delete.
 *
 * ACL is enforced both client-side (redirect to /) and server-side
 * (every PATCH/POST/DELETE returns 403 to non-admins).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Building2, Plus, Search, RefreshCcw, Eye, Pencil,
  PauseCircle, PlayCircle, Archive, Trash2,
} from 'lucide-react';
import { GlassCard, SectionHeader, PageLoading } from '../components/shared';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { madrassaApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

const STATUS_META = {
  pending:   { fr: 'En attente', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  active:    { fr: 'Active',     cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  suspended: { fr: 'Suspendue',  cls: 'bg-orange-500/15 text-orange-300 border-orange-500/30' },
  rejected:  { fr: 'Rejetée',    cls: 'bg-rose-500/15 text-rose-300 border-rose-500/30' },
  archived:  { fr: 'Archivée',   cls: 'bg-surface-elevated text-text-muted border-border' },
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.pending;
  return (
    <Badge className={`${meta.cls} border text-[10px]`} data-status={status}>
      {meta.fr}
    </Badge>
  );
};

// ---------------------------------------------------------------------------
// Form dialog used both for create + edit
// ---------------------------------------------------------------------------
const MadrassaFormDialog = ({
  initial = null,         // null = create mode, object = edit mode
  trigger,
  onDone,
  testidPrefix = 'admin-madrassa-form',
}) => {
  const [open, setOpen] = useState(false);
  const isEdit = !!initial;
  const [form, setForm] = useState({
    name: initial?.name || '',
    description: initial?.description || '',
    country: initial?.country || '',
    city: initial?.city || '',
    admin_email: '',
    status: initial?.status || 'active',
  });
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      name: initial?.name || '',
      description: initial?.description || '',
      country: initial?.country || '',
      city: initial?.city || '',
      admin_email: '',
      status: initial?.status || 'active',
    });
    setError(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial?.id]);

  const submit = async () => {
    if (!form.name.trim()) { setError('Nom requis.'); return; }
    setSubmitting(true);
    setError(null);
    try {
      if (isEdit) {
        await madrassaApi.adminUpdate(initial.id, {
          name: form.name.trim(),
          description: form.description.trim() || null,
          country: form.country.trim() || null,
          city: form.city.trim() || null,
          status: form.status,
        });
      } else {
        await madrassaApi.adminCreate({
          name: form.name.trim(),
          description: form.description.trim() || null,
          country: form.country.trim() || null,
          city: form.city.trim() || null,
          admin_email: form.admin_email.trim() || null,
        });
      }
      setOpen(false);
      onDone && onDone();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur lors de l\'enregistrement.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent data-testid={`${testidPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Modifier la madrassa' : 'Nouvelle madrassa'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <Input
            placeholder="Nom de la madrassa *"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid={`${testidPrefix}-name`}
          />
          <Textarea
            placeholder="Description (optionnel)"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={2}
            data-testid={`${testidPrefix}-description`}
          />
          <div className="grid grid-cols-2 gap-2">
            <Input
              placeholder="Ville"
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
              data-testid={`${testidPrefix}-city`}
            />
            <Input
              placeholder="Pays"
              value={form.country}
              onChange={(e) => setForm({ ...form, country: e.target.value })}
              data-testid={`${testidPrefix}-country`}
            />
          </div>
          {!isEdit && (
            <Input
              type="email"
              placeholder="Email du futur admin de madrassa (optionnel)"
              value={form.admin_email}
              onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
              data-testid={`${testidPrefix}-admin-email`}
            />
          )}
          {isEdit && (
            <Select
              value={form.status}
              onValueChange={(v) => setForm({ ...form, status: v })}
            >
              <SelectTrigger data-testid={`${testidPrefix}-status`}>
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="suspended">Suspendue</SelectItem>
                <SelectItem value="archived">Archivée</SelectItem>
                <SelectItem value="pending">En attente</SelectItem>
              </SelectContent>
            </Select>
          )}
          {isEdit && (
            <p className="text-[11px] text-text-muted">
              Slug : <code>{initial?.slug}</code> (lecture seule pour préserver les liens existants).
            </p>
          )}
          {error && (
            <p className="text-sm text-rose-300" data-testid={`${testidPrefix}-error`}>
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => setOpen(false)}
              data-testid={`${testidPrefix}-cancel`}
            >
              Annuler
            </Button>
            <Button
              onClick={submit}
              disabled={submitting}
              data-testid={`${testidPrefix}-submit`}
            >
              {submitting ? 'Enregistrement…' : (isEdit ? 'Enregistrer' : 'Créer')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Row + actions
// ---------------------------------------------------------------------------
const MadrassaRow = ({ m, onEditDone, onChangeStatus, onDelete }) => {
  const stats = m.stats || {};
  const navigate = useNavigate();
  const dateShort = m.created_at ? m.created_at.slice(0, 10) : '—';
  return (
    <GlassCard data-testid={`admin-madrassa-row-${m.id}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-9 h-9 rounded-md bg-primary/15 text-primary flex items-center justify-center shrink-0">
          <Building2 className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-[180px]">
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to={`/admin/madrassas/${m.id}`}
              className="text-sm font-medium text-text-primary hover:text-primary truncate"
              data-testid={`admin-madrassa-row-${m.id}-name`}
            >
              {m.name}
            </Link>
            <StatusBadge status={m.status} />
          </div>
          <div className="flex items-center gap-2 mt-1 flex-wrap text-[11px] text-text-muted">
            {m.city && <span>{m.city}</span>}
            {m.city && m.country && <span>·</span>}
            {m.country && <span>{m.country}</span>}
            {(m.city || m.country) && <span>·</span>}
            <span>Créée: {dateShort}</span>
          </div>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-text-secondary flex-wrap">
          <span data-testid={`admin-madrassa-row-${m.id}-stats-classes`}>
            {stats.classes ?? 0} classes
          </span>
          <span data-testid={`admin-madrassa-row-${m.id}-stats-teachers`}>
            {stats.teachers ?? 0} profs
          </span>
          <span data-testid={`admin-madrassa-row-${m.id}-stats-students`}>
            {stats.students ?? 0} élèves
          </span>
          {(stats.pendingTeacherRequests ?? 0) > 0 && (
            <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 border text-[10px]"
                   data-testid={`admin-madrassa-row-${m.id}-stats-pending`}>
              {stats.pendingTeacherRequests} demande{stats.pendingTeacherRequests > 1 ? 's' : ''}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => navigate(`/admin/madrassas/${m.id}`)}
            data-testid={`admin-madrassa-row-${m.id}-detail`}
            title="Voir le détail"
          >
            <Eye className="w-4 h-4" />
          </Button>
          <MadrassaFormDialog
            initial={m}
            onDone={onEditDone}
            testidPrefix={`admin-madrassa-row-${m.id}-edit`}
            trigger={
              <Button
                size="sm"
                variant="ghost"
                title="Modifier"
                data-testid={`admin-madrassa-row-${m.id}-edit-open`}
              >
                <Pencil className="w-4 h-4" />
              </Button>
            }
          />
          {m.status === 'active' && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onChangeStatus(m, 'suspended')}
              title="Suspendre"
              data-testid={`admin-madrassa-row-${m.id}-suspend`}
            >
              <PauseCircle className="w-4 h-4 text-amber-300" />
            </Button>
          )}
          {m.status === 'suspended' && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onChangeStatus(m, 'active')}
              title="Réactiver"
              data-testid={`admin-madrassa-row-${m.id}-reactivate`}
            >
              <PlayCircle className="w-4 h-4 text-emerald-300" />
            </Button>
          )}
          {m.status !== 'archived' && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onChangeStatus(m, 'archived')}
              title="Archiver"
              data-testid={`admin-madrassa-row-${m.id}-archive`}
            >
              <Archive className="w-4 h-4 text-text-muted" />
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDelete(m)}
            title="Supprimer"
            data-testid={`admin-madrassa-row-${m.id}-delete`}
          >
            <Trash2 className="w-4 h-4 text-rose-300" />
          </Button>
        </div>
      </div>
    </GlassCard>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export const AdminMadrassasPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_desc');

  const isAdmin = isAuthenticated && user?.role === 'admin';

  const refresh = async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const r = await madrassaApi.adminList({ with_stats: true });
      setItems(r.data?.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!isAdmin) {
      // Redirect non-admin users — UI defence; backend already returns 403.
      navigate('/', { replace: true });
      return;
    }
    refresh();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAdmin]);

  const countries = useMemo(() => {
    const set = new Set();
    items.forEach((m) => m.country && set.add(m.country));
    return [...set].sort();
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let arr = items.filter((m) => {
      if (q && !((m.name || '').toLowerCase().includes(q)
                 || (m.slug || '').toLowerCase().includes(q)
                 || (m.city || '').toLowerCase().includes(q))) return false;
      if (statusFilter !== 'all' && m.status !== statusFilter) return false;
      if (countryFilter !== 'all' && m.country !== countryFilter) return false;
      return true;
    });
    arr.sort((a, b) => {
      switch (sortBy) {
        case 'name_asc':    return (a.name || '').localeCompare(b.name || '');
        case 'name_desc':   return (b.name || '').localeCompare(a.name || '');
        case 'students_desc':
          return (b.stats?.students || 0) - (a.stats?.students || 0);
        case 'created_asc':
          return (a.created_at || '').localeCompare(b.created_at || '');
        case 'created_desc':
        default:
          return (b.created_at || '').localeCompare(a.created_at || '');
      }
    });
    return arr;
  }, [items, search, statusFilter, countryFilter, sortBy]);

  const onChangeStatus = async (m, newStatus) => {
    try {
      await madrassaApi.adminUpdate(m.id, { status: newStatus });
      refresh();
    } catch { /* surface a toast in a proper UI */ }
  };

  const onDelete = async (m) => {
    if (!window.confirm(
      `Supprimer "${m.name}" ?\n\nUne madrassa vide est supprimée définitivement. ` +
      `Si elle contient des classes/élèves/devoirs, elle sera ARCHIVÉE.`,
    )) return;
    try {
      const r = await madrassaApi.delete(m.id);
      if (r.data?.status === 'archived' && window.confirm(
        `${r.data?.message}\n\nForcer la suppression définitive (cascade) ?`,
      )) {
        await madrassaApi.delete(m.id, true);
      }
      refresh();
    } catch { /* ignore */ }
  };

  if (authLoading) return <PageLoading />;
  if (!isAdmin) return null; // useEffect already redirected

  const totals = {
    all: items.length,
    active: items.filter((m) => m.status === 'active').length,
    pending: items.filter((m) => m.status === 'pending').length,
    suspended: items.filter((m) => m.status === 'suspended').length,
    archived: items.filter((m) => m.status === 'archived').length,
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12"
         data-testid="admin-madrassas-page">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/')}
        className="mb-3"
        data-testid="admin-madrassas-back"
      >
        <ArrowLeft className="w-4 h-4 mr-1" /> Retour
      </Button>

      <SectionHeader
        overline="Console admin global"
        title="Madrassas partenaires"
        subtitle="Gérez la liste des madrassas, leur statut, et supervisez les demandes professeur."
      />

      <div className="mt-3">
        <Button asChild variant="outline" size="sm" data-testid="admin-madrassa-requests-link">
          <Link to="/admin/madrassa-requests">
            <Building2 className="w-3.5 h-3.5 mr-1" />
            Demandes de création de madrassa
          </Link>
        </Button>
      </div>

      {/* Toolbar */}
      <div className="mt-6 flex flex-wrap items-center gap-2"
           data-testid="admin-madrassas-toolbar">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
          <Input
            placeholder="Rechercher par nom, slug, ville…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
            data-testid="admin-madrassas-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]" data-testid="admin-madrassas-filter-status">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous statuts ({totals.all})</SelectItem>
            <SelectItem value="active">Actives ({totals.active})</SelectItem>
            <SelectItem value="pending">En attente ({totals.pending})</SelectItem>
            <SelectItem value="suspended">Suspendues ({totals.suspended})</SelectItem>
            <SelectItem value="archived">Archivées ({totals.archived})</SelectItem>
          </SelectContent>
        </Select>
        {countries.length > 0 && (
          <Select value={countryFilter} onValueChange={setCountryFilter}>
            <SelectTrigger className="w-[140px]" data-testid="admin-madrassas-filter-country">
              <SelectValue placeholder="Pays" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous pays</SelectItem>
              {countries.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[170px]" data-testid="admin-madrassas-sort">
            <SelectValue placeholder="Tri" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="created_desc">Plus récentes</SelectItem>
            <SelectItem value="created_asc">Plus anciennes</SelectItem>
            <SelectItem value="name_asc">Nom (A→Z)</SelectItem>
            <SelectItem value="name_desc">Nom (Z→A)</SelectItem>
            <SelectItem value="students_desc">Plus d'élèves</SelectItem>
          </SelectContent>
        </Select>
        <Button
          size="sm"
          variant="ghost"
          onClick={refresh}
          data-testid="admin-madrassas-refresh"
        >
          <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Rafraîchir
        </Button>
        <MadrassaFormDialog
          onDone={refresh}
          testidPrefix="admin-madrassas-create"
          trigger={
            <Button size="sm" data-testid="admin-madrassas-create-open">
              <Plus className="w-3.5 h-3.5 mr-1" /> Nouvelle madrassa
            </Button>
          }
        />
      </div>

      {/* List */}
      <div className="mt-6 space-y-2">
        {loading && (
          <p className="text-sm text-text-muted" data-testid="admin-madrassas-loading">
            Chargement…
          </p>
        )}
        {!loading && filtered.length === 0 && (
          <GlassCard data-testid="admin-madrassas-empty">
            <p className="text-sm text-text-muted text-center py-4">
              {items.length === 0
                ? "Aucune madrassa enregistrée. Cliquez sur « Nouvelle madrassa » pour en créer une."
                : "Aucune madrassa ne correspond aux filtres."}
            </p>
          </GlassCard>
        )}
        {!loading && filtered.map((m) => (
          <MadrassaRow
            key={m.id}
            m={m}
            onEditDone={refresh}
            onChangeStatus={onChangeStatus}
            onDelete={onDelete}
          />
        ))}
      </div>
    </div>
  );
};

export default AdminMadrassasPage;
